import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { api } from '../api/client.js';
import { isWorkerRole } from '../utils/roles.js';
import { getAlineaGuide } from '../data/alineaGuideCopy.js';
import { showJornadaGuiaTrial } from '../data/jornadaConversationScript.js';
import './AlineaGuideAgent.css';

function todayKey() {
    return new Date().toISOString().slice(0, 10);
}

export default function AlineaGuideAgent() {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(true);
    const [survey, setSurvey] = useState(null);

    const storageKey = `alinea_guide_open_${todayKey()}`;

    useEffect(() => {
        try {
            const saved = sessionStorage.getItem(storageKey);
            if (saved === '0') setOpen(false);
            if (saved === '1') setOpen(true);
        } catch {
            /* ignore */
        }
    }, [storageKey]);

    useEffect(() => {
        if (!isWorkerRole(user?.rol ?? user?.role)) return undefined;
        let cancelled = false;
        api.getTodaySurvey()
            .then((res) => {
                const payload =
                    res && (Object.prototype.hasOwnProperty.call(res, 'start') || Object.prototype.hasOwnProperty.call(res, 'end'))
                        ? res
                        : res?.data ?? res;
                if (!cancelled) setSurvey(payload);
            })
            .catch(() => {
                if (!cancelled) setSurvey(null);
            });
        return () => {
            cancelled = true;
        };
    }, [user, location.pathname]);

    const extras = useMemo(() => {
        const start = survey?.start || survey?.inicio || null;
        const end = survey?.end || survey?.fin || null;
        return {
            hasStartCheckin: Boolean(start?.feeling),
            hasEndCheckin: Boolean(end?.feeling || end?.fatigue || end?.closure),
            feeling: start?.feeling || null,
        };
    }, [survey]);

    const guide = useMemo(
        () => getAlineaGuide(user, location.pathname, extras),
        [user, location.pathname, extras]
    );

    if (!user || !guide) return null;
    if (location.pathname.startsWith('/jornada-guia')) return null;

    function toggle() {
        const next = !open;
        setOpen(next);
        try {
            sessionStorage.setItem(storageKey, next ? '1' : '0');
        } catch {
            /* ignore */
        }
    }

    return (
        <aside className={`alinea-guide ${open ? 'alinea-guide-open' : 'alinea-guide-min'}`} aria-label="Guía ALINEA">
            <button type="button" className="alinea-guide-toggle" onClick={toggle} aria-expanded={open}>
                <span className="alinea-guide-avatar" aria-hidden>
                    🤝
                </span>
                <span className="alinea-guide-toggle-text">
                    {open ? 'Guía ALINEA' : 'Guía: toca para abrir'}
                </span>
                <span className="alinea-guide-chevron" aria-hidden>
                    {open ? '▾' : '▸'}
                </span>
            </button>
            {open ? (
                <div className="alinea-guide-body">
                    <p className="alinea-guide-phase">Ahora: {guide.phase}</p>
                    <p className="alinea-guide-hello">{guide.hello}</p>
                    <p className="alinea-guide-tip">{guide.tip}</p>
                    {guide.next ? (
                        <Link className="alinea-guide-next" to={guide.next.to}>
                            {guide.next.label} →
                        </Link>
                    ) : null}
                    {isWorkerRole(user?.rol ?? user?.role) && showJornadaGuiaTrial(user) ? (
                        <p>
                            <Link className="alinea-guide-next" to="/jornada-guia">
                                Abrir conversación (prueba) →
                            </Link>
                        </p>
                    ) : null}
                </div>
            ) : null}
        </aside>
    );
}
